import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  UsersThree, UserPlus, Trash, Key, CheckCircle, WarningCircle,
  Crown, ShieldCheck, Eye, Copy,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

const ROLES = [
  { value: "admin",    label: "Master admin",   Icon: Crown,       desc: "Full access — orders, refunds, gifting, team management." },
  { value: "support",  label: "Support",        Icon: ShieldCheck, desc: "Everything except team management." },
  { value: "readonly", label: "Read-only",      Icon: Eye,         desc: "Can log in but every write is blocked." },
];

function RolePill({ role }) {
  const meta = ROLES.find((r) => r.value === role);
  if (!meta) return <span className="text-xs text-ink-500">{role}</span>;
  const bg = { admin: "bg-cobalt-50 text-cobalt-600",
               support: "bg-mint-soft text-mint",
               readonly: "bg-ink-100 text-ink-700" }[role];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${bg}`}>
      <meta.Icon size={12} weight="bold" /> {meta.label}
    </span>
  );
}

export default function AdminTeam() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("support");
  const [inviteResult, setInviteResult] = useState(null); // {email, password, created}
  const [submitting, setSubmitting] = useState(false);
  const [pwResult, setPwResult] = useState(null); // { email, password }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/team");
      setMembers(data.members || []);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load team");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setSubmitting(true); setInviteResult(null);
    try {
      const { data } = await api.post("/admin/team", {
        email: inviteEmail, name: inviteName || undefined, role: inviteRole,
      });
      setInviteResult({ email: data.member.email, password: data.password, created: data.created });
      setInviteEmail(""); setInviteName(""); setInviteRole("support");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Invite failed");
    } finally { setSubmitting(false); }
  };

  const changeRole = async (m, role) => {
    if (m.role === role) return;
    if (!window.confirm(`Change ${m.email}'s role to "${role}"?`)) return;
    try {
      await api.patch(`/admin/team/${m.id}`, { role });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
    }
  };

  const resetPassword = async (m) => {
    if (!window.confirm(`Generate a new password for ${m.email}? The previous password will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/team/${m.id}/reset-password`);
      setPwResult({ email: m.email, password: data.password });
    } catch (e) {
      alert(e.response?.data?.detail || "Reset failed");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Revoke back-office access for ${m.email}? Their account will be soft-demoted to a customer.`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Remove failed");
    }
  };

  const copy = (txt) => navigator.clipboard?.writeText(txt);

  const stats = useMemo(() => {
    const s = { admin: 0, support: 0, readonly: 0 };
    members.forEach((m) => { s[m.role] = (s[m.role] || 0) + 1; });
    return s;
  }, [members]);

  return (
    <div data-testid="admin-team">
      <div className="overline mb-2">{t("admin.team", "Team")}</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest">
          {t("admin.teamTitle", "Team members")}
        </h1>
        <button
          type="button"
          onClick={() => { setShowInvite(!showInvite); setInviteResult(null); }}
          className="btn-primary text-sm"
          data-testid="team-invite-toggle"
        >
          <UserPlus size={16} weight="bold" /> {t("admin.invite", "Invite member")}
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {ROLES.map((r) => (
          <div key={r.value} className="card-soft p-4 flex items-center gap-4" data-testid={`role-stat-${r.value}`}>
            <div className="w-11 h-11 rounded-2xl bg-cobalt-50 flex items-center justify-center text-cobalt-500 flex-shrink-0">
              <r.Icon size={20} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl tracking-tight">{stats[r.value] || 0}</div>
              <div className="text-xs text-ink-500 line-clamp-2">{r.label} · {r.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="card-soft p-6 mb-8" data-testid="team-invite-form">
          <h2 className="font-display font-bold text-lg mb-4">{t("admin.inviteTitle", "Invite a new team member")}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block sm:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">Email</span>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                     className="input-soft mt-1" placeholder="colleague@blueroam.io"
                     data-testid="invite-email" />
            </label>
            <label className="block sm:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">Name (optional)</span>
              <input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                     className="input-soft mt-1" data-testid="invite-name" />
            </label>
            <label className="block sm:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">Role</span>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                      className="input-soft mt-1" data-testid="invite-role">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-4 text-xs text-ink-500 bg-cobalt-50 rounded-2xl p-3 leading-relaxed">
            <b>Note:</b> a random 16-character password will be generated and shown to you once.
            The invited person will need to change it after their first login (recommended).
          </div>
          <button
            type="button"
            onClick={invite}
            disabled={!inviteEmail || submitting}
            className="btn-primary mt-4 disabled:opacity-50"
            data-testid="invite-submit"
          >
            {submitting ? "…" : <><UserPlus size={16} weight="bold" /> {t("admin.sendInvite", "Create member")}</>}
          </button>
          {inviteResult && (
            <div className="mt-4 rounded-2xl bg-mint-soft/40 border border-mint/30 p-4" data-testid="invite-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} weight="fill" className="text-mint" />
                <span className="font-semibold text-sm">
                  {inviteResult.created ? "Created" : "Promoted"} {inviteResult.email}
                </span>
              </div>
              <div className="text-xs mb-2">Temporary password (copy it now — you won&apos;t see it again):</div>
              <div className="flex items-center gap-2 bg-white rounded-xl p-2 border border-ink-100">
                <code className="text-sm font-mono flex-1 break-all">{inviteResult.password}</code>
                <button onClick={() => copy(inviteResult.password)} className="text-xs text-cobalt-600 hover:underline flex items-center gap-1">
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {pwResult && (
        <div className="mb-6 rounded-2xl bg-coral-soft/50 border border-coral p-4" data-testid="pw-reset-result">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={18} weight="fill" className="text-coral-deep" />
            <span className="font-semibold text-sm">Password reset for {pwResult.email}</span>
          </div>
          <div className="text-xs mb-2">New password (copy it now):</div>
          <div className="flex items-center gap-2 bg-white rounded-xl p-2 border border-ink-100">
            <code className="text-sm font-mono flex-1 break-all">{pwResult.password}</code>
            <button onClick={() => copy(pwResult.password)} className="text-xs text-cobalt-600 hover:underline flex items-center gap-1">
              <Copy size={12} /> Copy
            </button>
            <button onClick={() => setPwResult(null)} className="text-xs text-ink-500 hover:text-ink-900">✕</button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-2xl bg-red-50 text-destructive p-4 text-sm" data-testid="team-error">{error}</div>
      )}

      {/* Members table */}
      <div className="card-soft overflow-auto !rounded-3xl">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-cobalt-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Added</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-ink-500">Loading…</td></tr>
            )}
            {!loading && members.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-ink-500">
                <UsersThree size={28} className="text-ink-300 inline-block mb-2" /><br />
                No team members yet. Invite one above.
              </td></tr>
            )}
            {members.map((m) => {
              const isMe = m.id === user?.id;
              return (
                <tr key={m.id} className="border-t border-ink-100" data-testid={`team-row-${m.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{m.email}</div>
                    {isMe && <div className="text-[10px] uppercase text-cobalt-600 font-semibold tracking-widest">You</div>}
                  </td>
                  <td className="px-5 py-3 text-ink-700">{m.name || "—"}</td>
                  <td className="px-5 py-3">
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      disabled={isMe}
                      className="text-xs px-2 py-1 rounded-lg border border-ink-200 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid={`role-select-${m.id}`}
                      title={isMe ? "Ask another master admin to change your own role" : ""}
                    >
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <div className="mt-1"><RolePill role={m.role} /></div>
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-500">{fmtDate(m.created_at)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => resetPassword(m)}
                      className="text-xs font-semibold text-cobalt-600 hover:underline mr-4"
                      data-testid={`reset-pw-${m.id}`}
                    >
                      <Key size={12} weight="bold" className="inline mr-1" /> Reset password
                    </button>
                    <button
                      onClick={() => remove(m)}
                      disabled={isMe}
                      className="text-xs font-semibold text-destructive hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`remove-${m.id}`}
                    >
                      <Trash size={12} weight="bold" className="inline mr-1" /> Revoke access
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-xs text-ink-500 leading-relaxed">
        <b>Note on roles:</b> only <b>Master admins</b> can access this Team page. <b>Support</b> can do everything except manage team members. <b>Read-only</b> can log in but their session blocks every write operation — useful for accountants or advisors.
      </div>
    </div>
  );
}
